import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui';
import {
  CONTENT_BLOCKER_CLEAR_EVENT,
  CONTENT_BLOCKER_EVENT,
  emitContentBlockerSignal,
  isContentBlockerDetected,
  isElementCosmeticallyHidden,
  probeScriptBlocked
} from '../utils/contentBlockerSignals';
import './ContentBlockerNotice.css';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function getFocusableElements(root) {
  if (!root) {
    return [];
  }

  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
    return element.tabIndex >= 0 && element.getAttribute('aria-hidden') !== 'true';
  });
}

function setBackgroundInert(dialog) {
  const inerted = [];

  Array.from(document.body.children).forEach((child) => {
    if (child === dialog || child.contains(dialog) || child.hasAttribute('inert')) {
      return;
    }
    child.setAttribute('inert', '');
    inerted.push(child);
  });

  return () => {
    inerted.forEach((child) => child.removeAttribute('inert'));
  };
}

function ContentBlockerNotice({ onVisibilityChange }) {
  const baitRef = useRef(null);
  const dialogRef = useRef(null);
  const [visible, setVisible] = useState(() => isContentBlockerDetected());

  const reveal = useCallback(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [onVisibilityChange, visible]);

  useEffect(() => {
    const inspectBait = () => {
      if (isElementCosmeticallyHidden(baitRef.current)) {
        reveal();
      }
    };

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(inspectBait);
    });
    const timeoutId = window.setTimeout(inspectBait, 700);

    const onSignal = () => reveal();
    const onClear = () => setVisible(false);
    window.addEventListener(CONTENT_BLOCKER_EVENT, onSignal);
    window.addEventListener(CONTENT_BLOCKER_CLEAR_EVENT, onClear);

    let cancelled = false;
    const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
    if (scriptSrc) {
      probeScriptBlocked(scriptSrc).then((blocked) => {
        if (!cancelled && blocked) {
          emitContentBlockerSignal('psp_sdk');
        }
      });
    }

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener(CONTENT_BLOCKER_EVENT, onSignal);
      window.removeEventListener(CONTENT_BLOCKER_CLEAR_EVENT, onClear);
    };
  }, [reveal]);

  useLayoutEffect(() => {
    if (!visible) {
      return undefined;
    }

    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const restoreInert = setBackgroundInert(dialog);

    const focusDialog = () => {
      const preferred = dialog?.querySelector('.rg-sale-blocker-modal__action');
      const first = getFocusableElements(dialog)[0];
      (preferred || first || dialog)?.focus();
    };
    focusDialog();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key !== 'Tab' || !dialog) {
        return;
      }

      const focusable = getFocusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const leavesDialog = !dialog.contains(active);

      if (event.shiftKey && (active === first || leavesDialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || leavesDialog)) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (event) => {
      if (!dialog || dialog.contains(event.target)) {
        return;
      }
      event.preventDefault();
      focusDialog();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn);

    return () => {
      document.body.style.overflow = previousOverflow;
      restoreInert();
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [visible]);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <>
      <div className="rg-sale-blocker-probe" aria-hidden="true">
        <div ref={baitRef} className="adsbox" />
      </div>
      {visible && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={dialogRef}
              className="rg-sale-blocker-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rg-sale-blocker-title"
              aria-describedby="rg-sale-blocker-desc"
              tabIndex={-1}
            >
              <div className="rg-sale-blocker-modal__panel">
                <div className="rg-sale-blocker-modal__icon" aria-hidden="true">
                  <svg
                    className="rg-sale-blocker-modal__glyph"
                    viewBox="0 0 16 16"
                    width="28"
                    height="28"
                    fill="currentColor"
                  >
                    <path d="M5.338 1.59a61 61 0 0 0-2.837.856.48.48 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .213.032l.006.003c.02.003.05.008.08.008s.058-.005.08-.008l.006-.003a1 1 0 0 0 .213-.032q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.718-.306-1.73-.614-2.837-.855A61.5 61.5 0 0 0 8.002 1c-.687 0-1.368.1-2.006.24C5.99 1.38 5.662 1.49 5.338 1.59M7.001 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0m.05-5a.5.5 0 0 0-.5.5v2.5a.5.5 0 0 0 1 0V6.5a.5.5 0 0 0-.5-.5z" />
                  </svg>
                </div>
                <h2 id="rg-sale-blocker-title" className="rg-sale-blocker-modal__title">
                  No podés hacer ventas con el bloqueador activo
                </h2>
                <div id="rg-sale-blocker-desc" className="rg-sale-blocker-modal__body">
                  <p>
                    Detectamos una extensión que bloquea anuncios o scripts (Adblock, uBlock u
                    otra similar). Mientras esté encendida, <strong>no se pueden iniciar ni
                    completar ventas</strong>.
                  </p>
                  <p>
                    El bloqueador impide cargar el cobro y otras partes del flujo. Si seguís
                    igual, la venta puede quedar a medias o el pago no va a funcionar.
                  </p>
                  <ol className="rg-sale-blocker-modal__steps">
                    <li>Abrí el icono de la extensión en el navegador.</li>
                    <li>Pausala o desactivala para este sitio. Si tenés más de una, apagá todas.</li>
                    <li>Recargá la página para volver a intentar.</li>
                  </ol>
                  <p>
                    <a
                      className="rg-sale-blocker-modal__tutorial"
                      href="https://cirenio.com/ayuda/desactivar-adblocker"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver cómo desactivar el bloqueador
                    </a>
                  </p>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="rg-sale-blocker-modal__action"
                  autoFocus
                  onClick={handleReload}
                >
                  Ya lo desactivé, recargar
                </Button>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export default ContentBlockerNotice;
